<template>
    <div class="row related-products--block g-mb">
        <div class="col-span-main">
            <h2 class="mb-6 nm:mb-20">{{ heading }}</h2>
            <div class="related-products--wrapper">
                <product-card v-for="item in algoliaItems"
                              :key="item.objectID"
                              show-as="grid-view"
                              :item="item"
                              :logged-in="loggedIn"
                              :is-sales="false"
                              :in-sales-app="false"
                              :in-kiosk-app="false"
                              :is-best-b="false"
                              :user="user"
                              :sync-time="syncTime"
                              :login-url="loginUrl"
                              :default-price="item.price"
                              :price-fetched="item.priceFetched"
                              :volume-discounts="item.volumeDiscounts"
                              :products-in-order="savedOrCartProductIds"
                              :prop-qty="item.qty"
                              :direct-order="true"
                              @add-product-to-cart="addToCart"
                              @update-qty="updateQty"
                >
                </product-card>
            </div>
        </div>

        <div id="related-products" class="hidden"></div>
    </div>
</template>

<script>
import {relatedProducts} from "@algolia/recommend-js";
import recommend from "@algolia/recommend";
import {helpers} from "../js/mixins/helpers";
import {mapGetters, mapState} from "vuex";

export default {
    name: "RecommendedProducts",

    mixins: [helpers],

    props: {
        loggedIn: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: () => {
            }
        },
        syncTime: {
            type: String,
            default: ""
        },
        loginUrl: {
            type: String,
            default: "/login"
        },
        staticRelatedItems: {
            type: Array,
            default: () => []
        },
        favoriteProductIds: {
            type: Array,
            default: () => []
        },
        relatedObjectIds: {
            type: Array,
            default: () => []
        },
        fallbackCategory: {
            type: String,
            default: ""
        },
        heading: {
            type: String,
            default: ""
        }
    },

    components: {
        "product-card": () => import(/* webpackChunkName: "product-card" */ "../vue/components/ProductCard.vue")
    },

    data() {
        return {
            recommendClient: recommend(
                "VOYNA2HOMW",
                "a40c9abd596160f234d82e9c7bf3597b"
            ),
            algoliaItems: [],
            loading: true,
            objectIds: [],
            userFavorites: [],
            staticItems: [],
            totalItemsShown: 4
        };
    },

    mounted() {
        this.relatedObjectIds.forEach(id => {
            this.objectIds.push(id.toString());
        });
        this.userFavorites = this.favoriteProductIds;
        this.staticItems = this.staticRelatedItems;
        this.algoliaItems = this.staticItems;
        this.init();
    },

    methods: {
        async init() {
            let that = this;

            relatedProducts({
                container: "#related-products",
                recommendClient: this.recommendClient,
                indexName: this.indexName,
                objectIDs: this.objectIds,
                itemComponent: () => {
                },
                threshold: 0,
                maxRecommendations: this.algoliaItemsNeeded,
                fallbackParameters: {
                    facetFilters: [
                        `categories.lvl0:${this.fallbackCategory}`
                    ]
                },
                async transformItems(items) {
                    that.algoliaItems = [...that.algoliaItems, ...items];
                    await that.getPrices();
                    return [];
                }
            });
        },

        async getPrices() {
            let promises = this.algoliaItems.map(async (item) => {
                await this.getProductPrice(item);
            });
            await Promise.all(promises);
            this.loading = false;
        },

        async getProductPrice(item) {
            const response = await fetch(
                "/actions/ranson-module/customer/price",
                {
                    method: "POST",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": window.csrfToken
                    },
                    body: JSON.stringify({
                        useDb: true,
                        itemId: item.sku,
                        customer: this.user.id ?? null,
                        qty: item.qty,
                        requestedDeliveryDate: null
                    })
                }
            );

            const json = await response.json();

            if (json.success) {

                if (json.price !== 0) {
                    this.$set(item, "price", (json.price) ? this.formatCurrency(json.price) : false);
                }

                let lineItem = await this.getLineItems.find(lineItem => lineItem.snapshot.productId === item.objectID);
                let qty = lineItem ? lineItem.qty : 1;

                this.$set(item, "qty", qty);
                this.$set(item, "priceFetched", true);
                this.$set(item, "volumeDiscounts", json.volumeDiscounts);

            }
        },

        addToCart($event, currentProduct) {

            let form = $event.target.closest("form");
            let qty = form.querySelector("input[name=\"qty\"]").value;

            if (currentProduct && qty > 0) {
                window.axios({
                    url: `${window.rootUrl}actions/commerce/cart/update-cart`,
                    method: "POST",
                    data: {
                        purchasableId: currentProduct.purchasableId,
                        qty: qty
                    }
                }).then(result => {
                    if (result.status === 200) {
                        // Show notification
                        this.$notify({
                            text: window.i18n.t("success.addedToCart"),
                            type: "success"
                        });

                        // Save cart
                        if (result?.data?.cart) {
                            this.$store.dispatch("saveCart", result.data.cart);
                        }

                    } else {
                        this.$notify({
                            title: window.i18n.t("errors.generalTitle"),
                            text: window.i18n.t("errors.notAddedToCart"),
                            type: "error"
                        });
                        console.error("Cart error.", result);
                    }
                }).catch((error) => {
                    this.$notify({
                        title: window.i18n.t("errors.generalTitle"),
                        text: window.i18n.t("errors.cartNotUpdated"),
                        type: "error"
                    });
                    console.error("Cart error.", error);
                });
            }
        },

        async updateQty(e, currentProduct, quantity) {
            // Get qty
            const qty = e ? parseInt(e.target.value) : quantity;
            // Get algolia item
            const algoliaItem = this.algoliaItems.find(item => item.objectID === currentProduct.objectID);
            // Set qty
            algoliaItem.qty = qty;
            await this.getProductPrice(algoliaItem);
        }
    },

    computed: {
        ...mapGetters([
                "getLineItems"
            ]
        ),

        savedOrCartProductIds() {
            return this.getLineItems.map(item => item.snapshot.product);
        },

        indexName() {
            return `products_${window.language}`;
        },

        algoliaItemsNeeded() {
            return this.totalItemsShown - this.staticItems.length;
        }
    }
};
</script>